/**
 * Anthropic Claude Direct Provider
 */

import { DirectProvider } from '../base/DirectProvider'
import type { AIResponseWithReason, ContinueContext } from '../../types'
import { getContinuePrompt } from '../../writing-utils/writing-prompts'

export class AnthropicProvider extends DirectProvider {
  name = 'Claude'
  
  constructor(apiKey: string, model = 'claude-3-haiku-20240307') {
    // Anthropic models have different max output token limits
    // Context window is 200K, but max_tokens (output) is limited to 4096
    super({
      apiKey,
      model,
      apiUrl: 'https://api.anthropic.com/v1/messages',
      maxTokens: 4096  // Anthropic max output tokens is 4096
    })
  }
  
  protected buildHeaders(): HeadersInit {
    return {
      'Content-Type': 'application/json',
      'x-api-key': this.apiKey,
      'anthropic-version': '2023-06-01',
      'anthropic-dangerous-direct-browser-access': 'true'
    }
  }
  
  protected buildRequestBody(systemPrompt: string, userPrompt: string): any {
    return {
      model: this.model,
      max_tokens: this.maxTokens,
      messages: [
        { role: 'user', content: userPrompt }
      ],
      system: systemPrompt,
      temperature: this.temperature
    }
  }
  
  protected extractTextFromResponse(data: any): string {
    return data.content[0].text
  }

  /**
   * Continue writing with streaming support
   */
  async continueStreaming(
    text: string, 
    context?: ContinueContext,
    onChunk?: (chunk: string) => void
  ): Promise<string | AIResponseWithReason> {
    const mode = context?.mode || 'sentence'
    const prompts = getContinuePrompt(mode, context)
    
    // Modify system prompt for streaming - request plain text only
    const streamingSystemPrompt = prompts.system.replace(
      /응답 형식:[\s\S]*$/m,
      '이어쓴 텍스트만 반환하세요. JSON 형식이나 추가 설명 없이 순수한 텍스트만 반환하세요.'
    )
    
    const requestBody = {
      model: this.model,
      max_tokens: this.maxTokens,
      messages: [
        { role: 'user', content: prompts.user(text) }
      ],
      system: streamingSystemPrompt,
      temperature: this.temperature,
      stream: true  // Enable streaming
    }

    const response = await fetch(this.apiUrl, {
      method: 'POST',
      headers: this.buildHeaders(),
      body: JSON.stringify(requestBody)
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Anthropic API error: ${response.status} - ${errorText}`)
    }

    const reader = response.body?.getReader()
    if (!reader) {
      throw new Error('Failed to get response reader')
    }

    const decoder = new TextDecoder()
    let fullText = ''

    try {
      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        const chunk = decoder.decode(value)
        const lines = chunk.split('\n').filter(line => line.trim() !== '')

        for (const line of lines) {
          // Anthropic uses Server-Sent Events format
          if (line.startsWith('data: ')) {
            const data = line.slice(6)
            
            try {
              const parsed = JSON.parse(data)
              
              // Handle different event types
              if (parsed.type === 'content_block_delta') {
                const content = parsed.delta?.text
                if (content) {
                  fullText += content
                  onChunk?.(content)
                }
              } else if (parsed.type === 'message_stop') {
                break
              }
            } catch (e) {
              // Skip invalid JSON
            }
          }
        }
      }
    } finally {
      reader.releaseLock()
    }

    return {
      text: fullText,
      reason: undefined  // Continue is generation, not modification
    }
  }
  
  /**
   * Process custom prompt with streaming support
   */
  async processCustomPromptStreaming(
    text: string, 
    userPrompt: string, 
    mode?: 'sentence' | 'paragraph',
    onChunk?: (chunk: string) => void
  ): Promise<AIResponseWithReason> {
    // Check if this is a generation request (empty text) or modification request
    const isGeneration = !text || text.trim() === ''
    
    let systemPrompt: string
    let fullUserPrompt: string
    
    if (isGeneration) {
      // Text generation mode
      systemPrompt = `한국어 텍스트를 생성하는 전문 작가입니다.`
      
      if (mode === 'sentence') {
        systemPrompt += ' 한 문장으로 간결하게 작성하세요.'
      } else if (mode === 'paragraph') {
        systemPrompt += ' 3-5 문장으로 구성된 한 문단으로 작성하세요.'
      }
      
      systemPrompt += ' 생성된 텍스트만 반환하세요. JSON 형식이나 추가 설명 없이 순수한 텍스트만 반환하세요.'
      fullUserPrompt = `요청사항: ${userPrompt}`
    } else {
      // Text modification mode
      systemPrompt = `다음 텍스트를 사용자의 요청에 따라 수정해주세요.`
      
      if (mode === 'sentence') {
        systemPrompt += ' 한 문장으로 간결하게 작성하세요.'
      } else if (mode === 'paragraph') {
        systemPrompt += ' 한 문단으로 자세하게 작성하세요.'
      }
      
      systemPrompt += ' 수정된 텍스트만 반환하세요. JSON 형식이나 추가 설명 없이 순수한 텍스트만 반환하세요.'
      fullUserPrompt = `원문: ${text}\n\n요청사항: ${userPrompt}`
    }
    
    const response = await fetch(this.apiUrl, {
      method: 'POST',
      headers: this.buildHeaders(),
      body: JSON.stringify({
        model: this.model,
        max_tokens: this.maxTokens,
        temperature: this.temperature,
        system: systemPrompt,
        messages: [
          { role: 'user', content: fullUserPrompt }
        ],
        stream: true  // Enable streaming
      })
    })

    if (!response.ok) {
      const errorText = await response.text()
      throw new Error(`Anthropic API error: ${response.status} - ${errorText}`)
    }

    const reader = response.body?.getReader()
    if (!reader) {
      throw new Error('Failed to get response reader')
    }

    const decoder = new TextDecoder()
    let fullText = ''

    try {
      while (true) {
        const { done, value } = await reader.read()
        if (done) break

        const chunk = decoder.decode(value)
        const lines = chunk.split('\n').filter(line => line.trim() !== '')

        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6)
            if (data === '[DONE]') {
              break
            }
            
            try {
              const parsed = JSON.parse(data)
              
              // Anthropic uses different event types
              if (parsed.type === 'content_block_delta') {
                const content = parsed.delta?.text
                if (content) {
                  fullText += content
                  onChunk?.(content)
                }
              }
            } catch (e) {
              // Skip invalid JSON
            }
          }
        }
      }
    } finally {
      reader.releaseLock()
    }

    // Only provide reason for modifications, not for generations
    if (isGeneration) {
      return {
        text: fullText,
        reason: undefined
      }
    }

    return {
      text: fullText,
      reason: `프롬프트: "${userPrompt}"`
    }
  }
}